import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { toStoredPrompt } from "~/shared/lib/element-mentions";
import { NEW_CHAT_DRAFT_KEY, useDraftChatStore } from "~/shared/store/useDraftChatStore";

import { buildAttachmentsToSend, isEnterSubmit } from "./prompt-draft-helpers";

import type { AttachmentCreate } from "~/entities/chat";
import type { MentionableElement } from "~/shared/lib/element-mentions";
import type { PendingUpload } from "~/shared/store/types";
import type { KeyboardEvent } from "react";

/**
 * Общий потолок поля, когда собственного лимита у режима нет.
 *
 * Не «сколько примет модель», а защита от вставки романа в textarea: у режимов
 * с известным потолком (`ModeConfig.maxPromptLength`) действует он.
 */
export const MAX_LENGTH = 10_000;

/**
 * Сколько символов принимает выбранный режим.
 *
 * Потолок промпта у каждой пары «модель × провайдер» свой (у Kling 3.0 — 3072 на
 * apimart и 2500 на kie.ai), и исполнителя отбирают по нему: набранный сверх
 * лимита промпт оставлял запрос вовсе без исполнителя — цена не считалась, а
 * причина человеку видна не была. Берём максимум, объявленный режимом: это самый
 * длинный промпт, который хоть один подключённый провайдер исполнит.
 */
export function promptLimitOf(maxPromptLength: number | null | undefined): number {
    return maxPromptLength && maxPromptLength > 0 ? Math.min(maxPromptLength, MAX_LENGTH) : MAX_LENGTH;
}

export interface UsePromptDraftParams {
    /** Чат, которому принадлежит черновик; `null` — пустой экран до создания чата. */
    chatId: string | null;
    items: readonly PendingUpload[];
    onSubmit: (prompt: string, attachments: AttachmentCreate[]) => Promise<void> | void;
    disabled?: boolean;
    hasRequiredFiles: boolean;
    /** Выбранные элементы библиотеки — по ним `@Имя` в поле превращается в ссылку по id. */
    elements?: readonly MentionableElement[];
    /** Потолок промпта выбранного режима; `null` — режим его не объявляет. */
    maxPromptLength?: number | null;
}

/**
 * Инкапсулирует draft/send-концерн промпт-бокса: текст черновика + авторесайз
 * textarea, флаг sending, отправку (сбор залитых attachments → onSubmit)
 * и клавиатурный submit. Ветвящаяся логика (маппинг attachments,
 * Enter-предикат) — в чистых `./prompt-draft-helpers`.
 *
 * Сам текст живёт в `useDraftChatStore` рядом с вложениями и элементами, а не в
 * локальном стейте: отправка из пустого экрана уводит на `/chat/<id>` и
 * размонтирует бар, поэтому локальный стейт терял набранное ровно там, где
 * вложения выживали.
 */
export function usePromptDraft({
    chatId,
    items,
    onSubmit,
    disabled,
    hasRequiredFiles,
    elements = [],
    maxPromptLength = null,
}: UsePromptDraftParams) {
    const draftKey = chatId ?? NEW_CHAT_DRAFT_KEY;
    const text = useDraftChatStore((s) => s.textByChat[draftKey] ?? "");
    const setDraftText = useDraftChatStore((s) => s.setDraftText);
    const setText = useCallback((value: string) => setDraftText(draftKey, value), [draftKey, setDraftText]);
    const [sending, setSending] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useLayoutEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    }, [text]);

    const canSend = text.trim().length > 0 && !sending && disabled !== true && hasRequiredFiles;

    const promptLimit = promptLimitOf(maxPromptLength);
    const onTextChange = useCallback(
        (value: string) => {
            setText(value.slice(0, promptLimit));
        },
        [setText, promptLimit],
    );

    const handleSend = useCallback(async () => {
        if (!canSend) return;
        // В поле человек видит и правит `@Имя`, хранится же ссылка по id: имя
        // подставляется на лету, поэтому переименование элемента не рассыпает
        // ни историю, ни повтор запроса.
        const trimmed = toStoredPrompt(text.trim(), elements);
        const attachmentsToSend = buildAttachmentsToSend(items);
        setSending(true);
        try {
            await onSubmit(trimmed, attachmentsToSend);
            // Текст промпта и pending-вложения намеренно НЕ очищаем
            // после отправки — пользователь может внести минимум правок и сразу
            // запустить новую генерацию. Очистка — только по явному действию
            // (очистить поле / удалить вложение / уйти из чата).
        } finally {
            setSending(false);
        }
    }, [canSend, text, items, elements, onSubmit]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (isEnterSubmit(event)) {
                event.preventDefault();
                void handleSend();
            }
        },
        [handleSend],
    );

    const clearDraftText = useDraftChatStore((s) => s.clearDraftText);
    const handleClear = useCallback(() => clearDraftText(draftKey), [clearDraftText, draftKey]);

    return {
        text,
        textareaRef,
        canSend,
        onTextChange,
        handleSend,
        handleKeyDown,
        handleClear,
        /** Действующий потолок поля — бар показывает его, когда человек к нему подошёл. */
        promptLimit,
    };
}
